// Gate D2.4's numbers (engine/docs/spikes/002-green-valley/brief.md: "an audio spec that renders cues offline and
// reads levels and event counts"): scenarios rendered on an OfflineAudioContext with the page's own content (the same
// tables, the same engine and graph as play), each returning plain numbers for the spec. No ears needed: a duck's
// depth, the peak under the limiter, the pool's busiest moment, the music's bar grid against its log, a wall's muffle,
// a caption per important play; since the music pass, the room's tail after one note (card 18) and every mood's peak
// and loudness at full volume.
import { createAudio, gainToDb, measure, MIXER_FAULTS, renderOffline, Svf, type AudioOptions, type BusId, type Vec3 } from "@rpg/audio";
import type { SpikeContent } from "../content";
import { audioOptions } from "./content";
import { AUDIO_FAULTS, UNCAPTIONED_CUE } from "./faults";

/** The scenarios. */
export type OfflineName = "ducking" | "limiter" | "voices" | "music" | "muffle" | "captions" | "reverb" | "moods";

const SR = 22050;
// equal-power panning offline: an HRTF panner makes an OfflineAudioContext wait for its database, and one render
// of the whole spec hung there headless (the levels these scenarios read don't depend on the panning model)
const options = (content: SpikeContent, tier: string): AudioOptions => {
  const o = audioOptions(content, tier, { strip: AUDIO_FAULTS.noCaption ? UNCAPTIONED_CUE : null });
  return { ...o, pool: { ...o.pool, hrtfWithin: 0 } };
};
const without = (o: AudioOptions, ...keys: ("music" | "ambience")[]): AudioOptions => {
  const c = { ...o };
  for (const k of keys) delete c[k];
  return c;
};

/** Render a scenario and read its numbers. */
export async function offlineScenario(name: OfflineName, content: SpikeContent, tier: string): Promise<Record<string, unknown>> {
  const base = options(content, tier);
  switch (name) {
    case "ducking": {
      // a steady −30 dBFS tone on one bus (under the glue's threshold, so the master is linear), a babbled line at
      // 1.5 s (the voice bus muted, so only the ducked bus is measured): the drop while the line is up, and after
      const one = async (bus: BusId) => {
        const r = await renderOffline(without(base, "music", "ambience"), {
          seconds: 8,
          sampleRate: SR,
          events: [
            {
              at: 0,
              run: (a) => {
                a.volume("voice", 0);
                a.volume("master", 1);
                a.volume(bus, 1);
                const ctx = a.context!;
                const osc = ctx.createOscillator();
                osc.frequency.value = 440;
                const g = ctx.createGain();
                g.gain.value = 0.03;
                osc.connect(g).connect(a.mixer!.input[bus]);
                osc.start(0);
              },
            },
            { at: 1.5, run: (a) => void a.say({ speaker: "elder", text: "The ruins have stirred again; take care on the road.", seconds: 3.2 }) },
          ],
        });
        const before = measure(r.buffer, 0.5, 1.4).rmsDb;
        const under = measure(r.buffer, 2.3, 3.2).rmsDb;
        const after = measure(r.buffer, 7, 7.9).rmsDb;
        return { before, under, after, duck: under - before, released: after - before, ducks: r.audio.stats().ducks };
      };
      return { music: await one("music"), effects: await one("effects"), ambience: await one("ambience") };
    }
    case "limiter": {
      // everything loud at once: every cue five times at +12 dB on the listener, all volumes full
      const cues = Object.keys(base.sounds).filter((id) => base.sounds[id]!.bus !== "ambience" || !base.sounds[id]!.recipe.loop);
      const r = await renderOffline(without(base, "music", "ambience"), {
        seconds: 3,
        sampleRate: SR,
        events: [
          {
            at: 0.2,
            run: (a) => {
              for (const b of ["master", "music", "effects", "ambience", "voice", "ui"] as const) a.volume(b, 1);
              for (let k = 0; k < 5; k++) for (const c of cues) a.play(c, { gain: 12, at: 0.2 + k * 0.01 });
            },
          },
        ],
      });
      const m = measure(r.buffer);
      return { cues: cues.length * 5, peak: m.peak, peakDb: m.peakDb, over: m.over, rmsDb: m.rmsDb };
    }
    case "voices": {
      // 60 one-shots around the listener at once, then 200 loop emitters on a walk: the pool and the loop cap hold
      let x = 0;
      const r = await renderOffline(without(base, "music", "ambience"), {
        seconds: 6,
        sampleRate: SR,
        events: [
          {
            at: 0.1,
            run: (a) => {
              a.listener([0, 0, 0], [0, 0, -1]);
              for (let i = 0; i < 60; i++) {
                const ang = (i / 60) * Math.PI * 2;
                const d = 3 + (i % 20) * 1.5;
                a.play(i % 2 ? "hit.light" : "hit.heavy", { pos: [Math.sin(ang) * d, 1, Math.cos(ang) * d] });
              }
            },
          },
          {
            at: 1,
            run: (a) => {
              for (let i = 0; i < 200; i++) a.loop(`e${i}`, "amb.brook", [i * 3 - 300, 0, (i % 7) * 4]);
            },
          },
          ...Array.from({ length: 40 }, (_, k) => ({
            at: 1.05 + k * 0.1,
            run: (a: ReturnType<typeof createAudio>) => {
              x = -300 + k * 15;
              a.listener([x, 0, 0], [1, 0, 0]);
            },
          })),
        ],
      });
      const s = r.audio.stats();
      return { size: s.voices.size, peakBusy: s.voices.peakBusy, granted: s.voices.granted, stolen: s.voices.stolen, culled: s.voices.culled, loops: s.loops, tier };
    }
    case "music": {
      // the exploring mood; an enemy turns alert at 4.3 s; the fight is won at 13.7 s
      const r = await renderOffline(without(base, "ambience"), {
        seconds: 22,
        sampleRate: SR,
        events: [
          { at: 4.3, run: (a) => a.music.alert(true) },
          { at: 13.7, run: (a) => a.music.victory() },
        ],
      });
      const dir = r.audio.music.director()!;
      const log = dir.log.map((e) => ({ ...e }));
      const into = log.find((e) => e.kind === "combat-in") ?? null;
      const win = log.find((e) => e.kind === "victory") ?? null;
      const bar = dir.barLength();
      const offGrid = (t: number): number => Math.abs((t / bar) - Math.round(t / bar)) * bar;
      return {
        bar,
        log,
        combatIn: into ? { at: into.at, asked: into.asked, offGrid: offGrid(into.at), wait: into.at - into.asked } : null,
        victory: win ? { at: win.at, asked: win.asked, offGrid: offGrid(win.at), wait: win.at - win.asked } : null,
        counts: dir.state().counts,
        rmsExplore: measure(r.buffer, 1, 4).rmsDb,
        rmsCombat: into ? measure(r.buffer, into.at + 0.05, Math.min(into.at + 4, 13.5)).rmsDb : null,
        peak: measure(r.buffer).peak,
      };
    }
    case "muffle": {
      // a parry's ring 5 m ahead and to the right in the open, and behind a wall (the occlusion function says so), quiet enough
      // that the master stays linear (under the glue compressor's threshold)
      const one = async (wall: number) => {
        const r = await renderOffline({ ...without(base, "music", "ambience"), occlusion: () => wall }, {
          seconds: 1.5,
          sampleRate: 44100,
          events: [{ at: 0.1, run: (a) => (a.listener([0, 0, 0], [0, 0, -1]), void a.play("hit.parry", { pos: [3, 0, -4] as Vec3, variant: 0, gain: -18 })) }],
        });
        const m = measure(r.buffer, 0.1, 1.4);
        // the energy over 3 kHz (the ring's upper partials): a wall's low-pass takes it away
        // both channels (an equal-power panner puts a source to one side mostly in one channel)
        let e = 0;
        let n = 0;
        for (let c = 0; c < r.buffer.numberOfChannels; c++) {
          const x = r.buffer.getChannelData(c).subarray(Math.round(0.1 * 44100), Math.round(1.4 * 44100));
          const hp = new Svf(44100);
          hp.set(3000, 0.707);
          for (let i = 0; i < x.length; i++) {
            const y = hp.hp(x[i]!);
            e += y * y;
          }
          n += x.length;
        }
        return { rmsDb: m.rmsDb, highDb: gainToDb(Math.sqrt(e / n)), muffled: r.audio.stats().muffled };
      };
      const open = await one(0);
      const behind = await one(1);
      return { open, behind, drop: behind.rmsDb - open.rmsDb, highDrop: behind.highDb - open.highDb };
    }
    case "reverb": {
      // one guitar note (a short release, no echo) on a silent mood: its level while it sounds, and the music bus's
      // level just after the note's source has stopped (six release time constants on): what's left is the room
      const o = base.music!;
      const silent = { ...o, table: { ...o.table, moods: [{ id: "silent", bpm: 60, beatsPerBar: 4, root: 62, scale: [0], progression: [[0]] }] }, mood: "silent" };
      const inst = o.instruments.guitar!;
      const end = 0.5 + 0.4;
      const r = await renderOffline(
        { ...without(base, "ambience"), music: silent },
        {
          seconds: 4.5,
          sampleRate: SR,
          events: [
            {
              at: 0,
              run: (a) => {
                a.volume("master", 1);
                a.volume("music", 1);
              },
            },
            { at: 0.45, run: (a) => a.music.note({ at: 0.5, inst: "guitar", midi: 64, vel: 0.8, dur: 0.4 }) },
          ],
        },
      );
      const dryGone = end + (inst.release ?? 0.12) * 6 + 0.05;
      const note = measure(r.buffer, 0.5, end).rmsDb;
      const tail = measure(r.buffer, dryGone, dryGone + 0.5).rmsDb;
      const late = measure(r.buffer, 4.0, 4.5).rmsDb;
      return { note, tail, late, tailBelowNote: tail - note, dryGone, space: !!r.audio.mixer?.space, peak: measure(r.buffer).peak };
    }
    case "moods": {
      // each mood for 20 s at full volume (master and music 1), and explore with the combat layer and the victory
      // phrase: the peaks stay under the ceiling and each mood has its own level; then the same with the master's
      // compressor, limiter and ceiling bypassed (the raw peak): the music's own headroom, not the limiter's work
      const out: Record<string, { peak: number; peakDb: number; over: number; rmsDb: number; notes: number; rawPeak: number }> = {};
      const runs: { id: string; mood: string; alertAt?: number; victoryAt?: number }[] = [
        { id: "explore", mood: "explore" },
        { id: "town", mood: "town" },
        { id: "night", mood: "night" },
        { id: "combat", mood: "explore", alertAt: 4, victoryAt: 15 },
      ];
      const render = (x: (typeof runs)[number]) =>
        renderOffline(
          { ...without(base, "ambience"), music: { ...base.music!, mood: x.mood } },
          {
            seconds: 20,
            sampleRate: SR,
            events: [
              {
                at: 0,
                run: (a) => {
                  a.volume("master", 1);
                  a.volume("music", 1);
                },
              },
              ...(x.alertAt !== undefined ? [{ at: x.alertAt, run: (a: ReturnType<typeof createAudio>) => a.music.alert(true) }] : []),
              ...(x.victoryAt !== undefined ? [{ at: x.victoryAt, run: (a: ReturnType<typeof createAudio>) => a.music.victory() }] : []),
            ],
          },
        );
      for (const x of runs) {
        const r = await render(x);
        const m = measure(r.buffer);
        const c = r.audio.music.director()!.state().counts;
        const was = MIXER_FAULTS.limiterBypassed;
        MIXER_FAULTS.limiterBypassed = true;
        let raw: number;
        try {
          raw = measure((await render(x)).buffer).peak;
        } finally {
          MIXER_FAULTS.limiterBypassed = was;
        }
        out[x.id] = { peak: m.peak, peakDb: m.peakDb, over: m.over, rmsDb: measure(r.buffer, 4, 20).rmsDb, notes: c.base + c.combat + c.sting, rawPeak: raw };
      }
      return out;
    }
    case "captions": {
      // every cue once: each important one sends exactly one caption, nothing else sends any (no render needed)
      const a = createAudio(without(base, "music", "ambience"));
      for (const id of Object.keys(base.sounds)) a.play(id, { pos: [3, 0, 0] });
      const s = a.stats();
      const important = Object.keys(base.sounds).filter((id) => base.sounds[id]!.important);
      return { important, played: Object.keys(s.plays).length, captions: s.captions, uncaptioned: s.uncaptioned, flagged: s.important };
    }
  }
}
